import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { registerPushNotifications } from '../lib/pushNotifications';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      if (data && data.role !== 'customer') {
        registerPushNotifications().catch(() => {});
      }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // PWA-safe auth: when the app comes back to the foreground (locked phone,
  // task-switched, etc.) re-verify the session — iOS standalone can drop the
  // auth cookie without warning, which would otherwise leave silent fetch
  // failures and an "empty orders" panel.
  useEffect(() => {
    const onVisibility = () => { if (document.visibilityState === 'visible') checkAuth(); };
    const onAuthExpired = () => setUser(false);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('majnu:auth-expired', onAuthExpired);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('majnu:auth-expired', onAuthExpired);
    };
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data);
    registerPushNotifications().catch(() => {});
    return data;
  };

  const customerLogin = async (formData, tableId) => {
    const { data } = await api.post('/auth/customer-login', { ...formData, table_id: tableId });
    setUser(data);
    return data;
  };

  // Read the walk-in guest_id for the currently-scanned table. Feb 2026:
  // guest_id is keyed per-QR (`majnu:guestId:<qr>`) for session isolation.
  // Falls back to the legacy global key for sessions opened before the
  // migration so signin can still adopt any orphaned guest orders.
  const readGuestIdForCurrentQr = () => {
    try {
      const qr = localStorage.getItem('majnu:lastQrCode') || '';
      const scoped = qr ? localStorage.getItem(`majnu:guestId:${qr}`) : null;
      return scoped || localStorage.getItem('majnu:guestId') || null;
    } catch { return null; }
  };

  const customerSignin = async (identifier, password, tableId) => {
    // Pull the device guest_id so backend can claim any orphan guest orders
    // made on this device by this person (matched by phone/email + device).
    const guestId = readGuestIdForCurrentQr();
    const { data } = await api.post('/auth/customer-signin', { identifier, password, table_id: tableId, guest_id: guestId });
    if (data?.multiple) return data;
    setUser(data);
    return data;
  };

  const customerSigninPick = async (identifier, password, accountId, tableId) => {
    const guestId = readGuestIdForCurrentQr();
    const { data } = await api.post('/auth/customer-signin-pick', { identifier, password, account_id: accountId, table_id: tableId, guest_id: guestId });
    setUser(data);
    return data;
  };

  const customerSignup = async ({ name, email, phone, password }) => {
    const { data } = await api.post('/auth/customer-signup', { name, email, phone, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(false);
  };

  // Memoise the context payload so consumers don't re-render whenever
  // AuthProvider itself re-renders for unrelated reasons. Only changes
  // when user / loading actually flip.
  const value = useMemo(
    () => ({ user, loading, login, customerLogin, customerSignin, customerSignup, customerSigninPick, logout, setUser, checkAuth }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
